import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import useKlassesMap from '@/data/hook/shared/useKlassesMap';
import useExamPendencyColumns, {
  ExamPendencyColumnKey,
} from '@/data/hook/shared/pendencies/tables/useExamPendencyColumns';
import { StudentExamOrdering } from '@/data/services/studentExamServices';
import { OrderingKey } from '@/models/Filter';
import StudentExam from '@/models/StudentExam';
import { SmartTable, SmartTableProps } from '@/components/ui/daisy';

import { StudentExamWithKlass } from './types';

type ExamPendenciesTableProps = Omit<
  SmartTableProps<StudentExamWithKlass, ExamPendencyColumnKey>,
  'columns' | 'getRowKey' | 'data'
> & {
  data: StudentExam[];
  ordering?: StudentExamOrdering;
  onToggleOrdering: (field: OrderingKey<StudentExamOrdering>) => void;
};

export default function ExamPendenciesTable({
  data,
  ordering,
  onToggleOrdering,
  className,
  ...smartTableProps
}: ExamPendenciesTableProps) {
  const columns = useExamPendencyColumns({
    ordering,
    onToggleOrdering,
  });

  const klassIds = useMemo(() => data.map(({ klassId }) => klassId), [data]);

  const { klassesMap, isLoading: isLoadingKlass } = useKlassesMap({ klassIds });

  const rows = useMemo(
    () =>
      data.map(studentExam => ({
        ...studentExam,
        sentAt: studentExam.finishedAt,
        klass: {
          ...klassesMap[studentExam.klassId],
          id: studentExam.klassId,
          isLoading: isLoadingKlass,
        },
      })),
    [data, klassesMap, isLoadingKlass],
  );

  return (
    <SmartTable
      columns={columns}
      data={rows}
      getRowKey={studentExam => studentExam.id}
      className={twMerge('table-fixed', className)}
      {...smartTableProps}
    />
  );
}
